import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Lock, LogOut, RefreshCw, Inbox, Mail, Phone } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HTTP_UNAUTHORIZED = 401;
const http = axios.create({ baseURL: API, withCredentials: true });

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const AdminPage = () => {
  const [authed, setAuthed] = useState(null);
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchBookings = useCallback(async (silent = false) => {
    setLoading(true);
    try {
      const res = await http.get('/admin/bookings');
      setBookings(res.data);
      setAuthed(true);
    } catch (err) {
      if (err?.response?.status === HTTP_UNAUTHORIZED) {
        if (!silent) toast({ title: 'Session expired', description: 'Please log in again.' });
        setAuthed(false);
      } else {
        toast({ title: 'Could not load bookings', description: 'Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings(true);
  }, [fetchBookings]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await http.post('/admin/login', creds);
      setCreds({ username: '', password: '' });
      fetchBookings();
    } catch {
      toast({ title: 'Login failed', description: 'Invalid username or password.' });
    }
  };

  const logout = async () => {
    try { await http.post('/admin/logout'); } catch { /* cookie cleared server-side anyway */ }
    setAuthed(false);
    setBookings([]);
  };

  if (authed === null) {
    return <div className="min-h-screen bg-cream" data-testid="admin-checking-session" />;
  }

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-5">
        <form onSubmit={login} className="bg-white rounded-3xl shadow-xl border border-black/[0.05] p-8 md:p-10 w-full max-w-md">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ background: 'var(--coral)' }}>
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-head font-extrabold text-2xl mb-1" style={{ color: 'var(--navy)' }}>Admin Login</h1>
          <p className="font-body text-sm mb-6" style={{ color: 'var(--ink)' }}>Sign in to view your booking requests.</p>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="font-head font-semibold" style={{ color: 'var(--navy)' }}>Username</Label>
              <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })}
                className="h-12 rounded-lg" placeholder="Username" data-testid="admin-username-input" />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-head font-semibold" style={{ color: 'var(--navy)' }}>Password</Label>
              <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })}
                className="h-12 rounded-lg" placeholder="Password" data-testid="admin-password-input" />
            </div>
            <button type="submit" data-testid="admin-login-btn" className="btn-coral font-head font-bold py-3.5 rounded-xl mt-2">Sign In</button>
          </div>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-white border-b border-black/[0.06] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Inbox className="w-6 h-6" style={{ color: 'var(--coral)' }} />
            <span className="font-head font-extrabold text-lg" style={{ color: 'var(--navy)' }}>Booking Requests</span>
            <span className="font-head font-bold text-xs px-2.5 py-0.5 rounded-full" style={{ background: 'var(--primary-light)', color: 'var(--navy)' }}>{bookings.length}</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => fetchBookings()} data-testid="admin-refresh-btn" className="flex items-center gap-2 font-head font-semibold text-sm px-3 py-2 rounded-md hover:bg-black/5 transition-colors" style={{ color: 'var(--navy)' }}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 font-head font-semibold text-sm px-3 py-2 rounded-md hover:bg-black/5 transition-colors" style={{ color: 'var(--coral)' }}>
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        {bookings.length === 0 ? (
          <EmptyState loading={loading} />
        ) : (
          <div className="grid gap-4">
            {bookings.map((bk) => (
              <div key={bk.id} className="bg-white rounded-2xl border border-black/[0.05] shadow-sm p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-head font-bold text-lg" style={{ color: 'var(--navy)' }}>{bk.name}</h3>
                    <p className="font-body text-sm" style={{ color: 'var(--ink)' }}>{bk.organization}</p>
                  </div>
                  <div className="text-right">
                    <span className="font-head font-bold text-xs px-2.5 py-1 rounded-full" style={{ background: 'var(--primary-lightest)', color: 'var(--navy)' }}>
                      {bk.eventType}
                    </span>
                    <p className="font-body text-xs mt-1.5" style={{ color: 'var(--ink)' }}>{fmtDate(bk.submittedAt)}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-3">
                  <a href={`mailto:${bk.email}`} className="flex items-center gap-1.5 font-body text-sm font-semibold" style={{ color: 'var(--coral)' }}>
                    <Mail className="w-4 h-4" /> {bk.email}
                  </a>
                  {bk.location && <span className="font-body text-sm" style={{ color: 'var(--ink)' }}>📍 {bk.location}</span>}
                  {bk.eventDate && <span className="font-body text-sm" style={{ color: 'var(--ink)' }}>🗓 {bk.eventDate}</span>}
                  {bk.audienceSize && <span className="font-body text-sm" style={{ color: 'var(--ink)' }}>👥 {bk.audienceSize}</span>}
                </div>

                {(bk.topics || bk.lookingFor || bk.additional || bk.hearAbout) && (
                  <button
                    onClick={() => setSelected(selected === bk.id ? null : bk.id)}
                    className="font-head font-semibold text-sm mt-3 hover:underline"
                    style={{ color: 'var(--primary-indigo)' }}
                  >
                    {selected === bk.id ? 'Hide details' : 'View details'}
                  </button>
                )}

                {selected === bk.id && (
                  <div className="mt-3 pt-3 border-t border-black/[0.06] grid gap-2.5 font-body text-sm" style={{ color: 'var(--ink)' }}>
                    {bk.topics && <p><strong style={{ color: 'var(--navy)' }}>Preferred topic:</strong> {bk.topics}</p>}
                    {bk.lookingFor && <p><strong style={{ color: 'var(--navy)' }}>Looking for:</strong> {bk.lookingFor}</p>}
                    {bk.additional && <p><strong style={{ color: 'var(--navy)' }}>Additional info:</strong> {bk.additional}</p>}
                    {bk.hearAbout && <p><strong style={{ color: 'var(--navy)' }}>Heard about via:</strong> {bk.hearAbout}</p>}
                    <p><strong style={{ color: 'var(--navy)' }}>Language:</strong> {String(bk.lang || 'en').toUpperCase()}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

const EmptyState = ({ loading }) => {
  if (loading) {
    return <p className="font-body text-center py-20" style={{ color: 'var(--ink)' }}>Loading…</p>;
  }
  return (
    <div className="text-center py-24" data-testid="admin-empty-state">
      <Inbox className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--primary-mid)' }} />
      <p className="font-head font-bold text-xl" style={{ color: 'var(--navy)' }}>No booking requests yet</p>
      <p className="font-body mt-1" style={{ color: 'var(--ink)' }}>New requests from your website will appear here.</p>
    </div>
  );
};

export default AdminPage;
